import React, { useState, useEffect } from "react";
import styled from "styled-components";
import LoadingScreen from "./LoadingScreen";
import AssessmentResult from "./AssessmentResult";
import { Scale } from 'react-scaling';

export default function NewGrammarGame({ ...props }) {
    const { children, correct, incorrect, loading, questionLimit, score, defaultTimer } = props;

    // ⏱️ Countdown clock tracks remaining seconds
    const [secondsLeft, setSecondsLeft] = useState(defaultTimer > 0 ? defaultTimer : 1200);

    useEffect(() => {
        if (loading || secondsLeft <= 0) return;

        const interval = setInterval(() => {
            setSecondsLeft(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [loading, secondsLeft]);

    if (loading) return <LoadingScreen />;

    const answeredTotal = (correct?.length || 0) + (incorrect?.length || 0);
    const isFinished = (questionLimit > 0 && answeredTotal >= questionLimit) || secondsLeft <= 0;

    const AreYouSureBack = () => {
        if (window.confirm("Are you sure you want to leave? Your progress will not be saved.")) {
            props.onBack();
        }
    };

    // Formats counter string display down to MM:SS matching the system schema
    const formatTime = (totalSeconds) => {
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <ApplicationContainer>
            <Scale scaleTo={1720}>
                <ApplicationHeader>
                    {/* 🌟 Top-Left: Pulled inward to sit close to the left side of the timer */}
                    <LeftHeader>
                        <Score>
                            {answeredTotal} / {questionLimit}
                        </Score>
                    </LeftHeader>
                    
                    <CenterHeader>
                        <TimerBox>
                            {formatTime(secondsLeft)}
                        </TimerBox>
                    </CenterHeader>
                    
                    {/* 🌟 Top-Right: Pulled inward to sit close to the right side of the timer */}
                    <RightHeader>
                        {/* Clean blocks without the tick and cross symbols, matching image_e1dea2.jpg */}
                        <CorrectBadge>{correct?.length || 0}</CorrectBadge>
                        <IncorrectBadge>{incorrect?.length || 0}</IncorrectBadge>
                        <Back onClick={AreYouSureBack}>Back</Back>
                    </RightHeader>
                </ApplicationHeader>
            </Scale>

            {!isFinished ? (
                <ContentArea>{children}</ContentArea>
            ) : (
                <AssessmentResult
                    score={score}
                    correct={correct}   
                    incorrect={incorrect} 
                    onBack={props.onBack}
                    feedback={props.feedback}
                    parent={props.parent || props} 
                />
            )}
        </ApplicationContainer>
    );
}

const ApplicationContainer = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    z-index: 1000;
    background: linear-gradient(180deg, #091220 0%, #1B2735 100%);
`;

const ApplicationHeader = styled.div`
    width: 100%;
    height: 60px;
    background: #683d8c;
    justify-content: center; /* Clusters rows tightly towards the center column area */
    display: flex;
`;

const LeftHeader = styled.div`
    width: 35%;
    display: flex;
    justify-content: flex-end; /* Aligns item right up against the left edge of the timer */
    align-items: center;
    padding-right: 35px; /* Creates clean spacing outside the timer box */
`;

const RightHeader = styled.div`
    width: 35%;
    display: flex;
    align-items: center;
    justify-content: flex-start; /* Aligns items right up against the right edge of the timer */
    padding-left: 35px; /* Creates clean spacing outside the timer box */
    gap: 12px; /* Uniform horizontal item gaps for badges and back button action controls */
`;

const CenterHeader = styled.div`
    width: 15%;
    display: flex;
    justify-content: center;
`;

const TimerBox = styled.div`
    width: 180px;
    height: 75px;
    background: #ef3e37;
    text-align: center;
    font-size: 3em;
    font-weight: 600;
    color: #fff;
    line-height: 75px;
    margin-top: 0px;
    border-radius: 0px 0px 15px 15px;
    z-index: 11;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
`;

const Score = styled.div`
    width: 160px;
    height: 40px;
    background: #4b2e6b;
    text-align: center;
    font-size: 1.8em;
    font-weight: 600;
    line-height: 40px;
    color: #fff;
    border-radius: 20px;
`;

const CorrectBadge = styled.div`
    width: 55px;
    height: 40px;
    background: #19a03f;
    text-align: center;
    font-size: 1.8em;
    font-weight: 600;
    line-height: 40px;
    color: #fff;
    border-radius: 6px;
`;

const IncorrectBadge = styled.div`
    width: 55px;
    height: 40px;
    background: #ef3e37;
    text-align: center;
    font-size: 1.8em;
    font-weight: 600;
    line-height: 40px;
    color: #fff;
    border-radius: 6px;
`;

const Back = styled.div`
    width: 130px;
    height: 40px;
    background: #4b2e6b;
    user-select: none;
    text-align: center;
    font-size: 2em;
    font-weight: 600;
    line-height: 40px;
    color: #fff;
    border-radius: 20px;
    cursor: pointer;
    margin-left: 8px;
    &:hover {
        background: #5a3882;
    }
`;

const ContentArea = styled.div`
    flex: 1;
    position: relative;
`;